import { PureComponent, Fragment } from 'react';
import { withTranslation } from 'react-i18next';
import { Pagination, Table, Icon } from 'semantic-ui-react';
import _ from 'lodash';

import { isEmpty, map } from '../../utils/lodash';
import { EmptyContainer } from '../../components';
import CopyText from '../copyText';

import './CustomTable.scss';

class CustomTableRow extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
    };
  }

  toggleOpen() {
    this.setState({ isOpen: !this.state.isOpen });
  }

  render() {
    const {
      item,
      idx,
      level,
      columns,
      keyExtractor,
      arrowed = true,
    } = this.props;
    const { isOpen } = this.state;

    if (isEmpty(item)) {
      return null;
    }

    const hasChildren = !isEmpty(item.children);
    const rowKey = (keyExtractor && keyExtractor(item, idx)) || idx;

    return (
      <Fragment key={`table_body_${rowKey || idx}`}>
        <Table.Row
          key={`table_body_row_levek${level}_${rowKey}`}
          className={`level-${level} ${level > 1 ? 'sub-data-table' : ''}`}
          onClick={() => {
            if (item.children) {
              this.toggleOpen();
            }
            if (this.props.onRowClick) {
              this.props.onRowClick(item);
            }
          }}
        >
          {map(columns, (column, c) => {
            const { isCopyEnabled } = column;

            return (
              <Table.Cell
                key={`table_body_row_cell_level${level}_${c}`}
                verticalAlign={column.verticalAlign || 'top'}
                textAlign={column.textAlign || 'left'}
                width={column.width || null}
                className={level > 1 ? 'sub-data-table' : ''}
              >
                <div
                  className={
                    level > 1 && c === 0
                      ? `sub-column sub-column-level-${level}`
                      : 'top-level-column'
                  }
                >
                  <div
                    className={arrowed && c === 0 ? 'arrow-first-column' : ''}
                  >
                    {arrowed && c === 0 && (
                      <Icon
                        name={`triangle ${isOpen ? 'down' : 'right'}`}
                        className={!hasChildren ? 'invisible' : ''}
                      />
                    )}
                    <span
                      id={
                        isCopyEnabled
                          ? `copyText_${column.render(item, level)}`
                          : ''
                      }
                    >
                      {column.render(item, level)}
                    </span>
                  </div>
                  {isCopyEnabled && (
                    <CopyText text={column.render(item, level)} />
                  )}
                </div>
              </Table.Cell>
            );
          })}
        </Table.Row>

        {item.children &&
          isOpen &&
          item.children.map((child, childIdx) => (
            <CustomTableRow
              {...this.props}
              item={child}
              idx={childIdx}
              level={level + 1}
            />
          ))}
      </Fragment>
    );
  }
}

class CustomTable extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      expandedRows: [],
      isOpen: false,
    };
  }

  renderEmptyRow() {
    const { t, columns, height, emptyMessage } = this.props;

    return (
      <Table.Row className="empty-row">
        <Table.Cell colSpan={columns.length}>
          <EmptyContainer
            classes={`no-data ${height ? 'more-height' : ''}`}
            msg={
              emptyMessage ? emptyMessage : `${t('common.no_records_found')}...`
            }
          />
        </Table.Cell>
      </Table.Row>
    );
  }

  render() {
    const {
      parentHeaders,
      header,
      columns,
      data,
      customClass,
      pagination,
      total = null,
      page,
      footerName = '',
      handlePaginationChange,
      noOfPages,
      customHeaderClass = '',
      headerFixed = false,
      currencySymbol = '$',
    } = this.props;

    return (
      <Table
        basic="very"
        stackable
        structured={false}
        className={customClass ? customClass : 'home-activity'}
      >
        {header ? (
          <Table.Header className={customHeaderClass} fullWidth>
            {parentHeaders ? (
              <>
                <Table.Row>
                  {map(parentHeaders, (column, i) => (
                    <Table.HeaderCell
                      key={`table_header_parent_header_cell_${i}`}
                      textAlign={column.textAlign ? column.textAlign : 'center'}
                      colSpan={column.colSpan ? column.colSpan : '1'}
                      className={column.className ? column.className : ''}
                    >
                      {column.title ? column.title : ''}
                    </Table.HeaderCell>
                  ))}
                </Table.Row>
              </>
            ) : null}
            <Table.Row className={headerFixed ? 'header-fixed' : ''}>
              {map(columns, (column, i) => (
                <Table.HeaderCell
                  key={`table_header_header_cell_${i}`}
                  textAlign={column.textAlign ? column.textAlign : 'left'}
                  className={column.className ? column.className : ''}
                  width={column.width ? column.width : null}
                >
                  {column.title ? column.title : ''}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
        ) : null}

        <Table.Body>
          {_.isEmpty(data)
            ? this.renderEmptyRow()
            : data.map((item, i) => (
                <CustomTableRow
                  key={this.props.keyExtractor?.(item, i) ?? i}
                  item={item}
                  idx={i}
                  level={1}
                  {...this.props}
                />
              ))}
        </Table.Body>

        {total !== null ? (
          <Table.Footer fullWidth>
            <Table.Row>
              <Table.HeaderCell colSpan="2">
                <b className="partner-total-column">{footerName}</b>
              </Table.HeaderCell>
              <Table.HeaderCell colSpan="3">
                <b className="partner-total-column">
                  {currencySymbol}
                  {parseFloat(total).toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    useGrouping: true,
                  })}
                </b>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        ) : null}

        {pagination && !isEmpty(data) ? (
          <Table.Footer>
            <Table.Row textAlign="center">
              <Table.HeaderCell colSpan={columns ? columns.length : 1}>
                <Pagination
                  className="table-pagination"
                  size="tiny"
                  activePage={page ? page : 1}
                  onPageChange={(e, { activePage }) =>
                    handlePaginationChange
                      ? handlePaginationChange(activePage)
                      : false
                  }
                  totalPages={noOfPages ? noOfPages : 1}
                  pointing
                  secondary
                  firstItem={null}
                  lastItem={null}
                  siblingRange={1}
                  stackable
                />
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        ) : null}
      </Table>
    );
  }
}

CustomTable = withTranslation()(CustomTable);

export { CustomTable };
